import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery, TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { ArrowsClockwise, Check, X, CaretLeft, CaretRight, CaretUp, CaretDown, Phone, MagnifyingGlass, Export } from '@phosphor-icons/react';
import { getIngestUrl, getApiUrl, authFetch } from '../config';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';

type KampagneTyp = 'CB' | 'IB' | 'KR' | 'UG';
type Tab = 'sync' | 'optins' | 'statistics' | 'wkz-statistik' | 'durchlaeufe';

interface Kampagne {
  typ: KampagneTyp;
  campaignId: string;
  label: string;
}

interface ImportRecord {
  id: number;
  kampagne_typ: KampagneTyp;
  telefon_monat: string;
  total_fetched: number;
  total_imported: number;
  optin_ja: number;
  optin_nein: number;
  status: string;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

interface SyncResult {
  kampagne: KampagneTyp;
  monat: string;
  importId: number;
  totalFetched: number;
  totalImported: number;
  optinJa: number;
  optinNein: number;
  durationMs: number;
  error?: string;
}

interface OptinRecord {
  id: number;
  wkz: string;
  contact_id: string;
  vnm: string;
  nnm: string;
  plz: string;
  ort: string;
  str: string;
  optin: string;
  optin_telefon: string;
  optin_email: string;
  optin_brief: string;
  optin_filename: string;
  campaign_id: string;
  contact_status: string;
  kampagne_typ: KampagneTyp;
  telefon_monat: string;
  email: string;
  geburtsdatum: string;
  telefon: string;
  handy: string;
  phone: string;
  anrede: string;
  titel: string;
  lkn: string;
  fc_task: string;
  fc_status: string;
  fc_status_detail: string;
  fc_date: string;
  fc_user: string;
  recording_url: string;
  optinort_alt: string;
  last_synced_at: string;
  aid: string;
  mub_plz: string;
  mub_ort: string;
  processing_errors: string;
}

interface WkzStatistikRow {
  wkz: string;
  total: number;
  success: number;
  declined: number;
  optin_ja: number;
  optin_nein: number;
  lose: number;
  agents: number;
  erster_tag: string | null;
  letzter_tag: string | null;
}

interface AbuntisJob {
  id: number;
  monat: string | null;
  status: string;
  filename: string | null;
  record_count: number | null;
  result_filename: string | null;
  result_size: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface RunEntry {
  type: 'sync' | 'abuntis';
  id: number;
  date: string;
  label: string;
  status: string;
  meta: Record<string, any>;
}

interface LogSummaryField {
  field_name: string;
  change_count: number;
  contact_count: number;
}

interface LogEntry {
  id: number;
  run_type: string;
  run_id: number;
  contact_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

type WkzSortKey = 'wkz' | 'total' | 'success' | 'declined' | 'optin_ja' | 'optin_nein' | 'lose' | 'agents' | 'erster_tag' | 'letzter_tag' | 'succ_pct' | 'ja_pct';
type SortDir = 'asc' | 'desc';

const KAMPAGNE_LABELS: Record<KampagneTyp, string> = {
  CB: 'Callbacks',
  IB: 'Inbound',
  KR: 'KüRü',
  UG: 'Upgrader',
};

const KAMPAGNE_COLORS: Record<KampagneTyp, string> = {
  CB: '#2563eb',
  IB: '#059669',
  KR: '#d97706',
  UG: '#7c3aed',
};

function getDefaultMonat(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function DialfireSyncPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState<Tab>('sync');
  const [monat, setMonat] = useState(getDefaultMonat());
  const [selectedKampagnen, setSelectedKampagnen] = useState<KampagneTyp[]>(['CB', 'IB', 'KR', 'UG']);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, { connected: boolean; error?: string; recordCount?: number }> | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);

  // OptIn Tab State
  const [optins, setOptins] = useState<OptinRecord[]>([]);
  const [optinTotal, setOptinTotal] = useState(0);
  const [optinPage, setOptinPage] = useState(1);
  const [optinLimit] = useState(50);
  const [optinFilterKampagne, setOptinFilterKampagne] = useState<string>('');
  const [optinFilterMonat, setOptinFilterMonat] = useState<string>('');
  const [optinFilterOptin, setOptinFilterOptin] = useState<string>('');
  const [optinSearch, setOptinSearch] = useState('');
  const [loadingOptins, setLoadingOptins] = useState(false);
  const [selectedOptin, setSelectedOptin] = useState<OptinRecord | null>(null);

  // Statistics Tab State
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // WKZ-Statistik Tab State
  const [wkzVon, setWkzVon] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [wkzBis, setWkzBis] = useState(() => new Date().toISOString().slice(0, 10));
  const [wkzRows, setWkzRows] = useState<WkzStatistikRow[]>([]);
  const [loadingWkz, setLoadingWkz] = useState(false);
  const [wkzSortKey, setWkzSortKey] = useState<WkzSortKey>('total');
  const [wkzSortDir, setWkzSortDir] = useState<SortDir>('desc');

  // Abuntis Export State
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ filename: string; recordCount: number; orderId?: number; triggerResponse?: string } | null>(null);
  const [abuntisFiles, setAbuntisFiles] = useState<{ name: string; size: number; modifyTime: number; staged?: boolean; status?: string }[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [pollStatus, setPollStatus] = useState<{ phase: string; message: string; attempt?: number }[]>([]);
  const [pollResultFile, setPollResultFile] = useState<{ name: string; size: number } | null>(null);
  const [importingFile, setImportingFile] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ filename: string; totalRows: number; updated: number; notFound: number; unchanged: number; errors: number; durationMs: number } | null>(null);

  // Wpool Datenübernahme
  const [wpMonat, setWpMonat] = useState(getDefaultMonat());
  const [wpOptinTyp, setWpOptinTyp] = useState<'generell' | 'brief' | 'telefon' | 'email'>('brief');
  const [wpHkn, setWpHkn] = useState('BOE08051');
  const [wpLfr, setWpLfr] = useState('BOE');
  const [wpInf2, setWpInf2] = useState(() => {
    const d = new Date();
    return 'Einspielung ' + d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
  });
  const [wpCount, setWpCount] = useState<number | null>(null);
  const [wpExporting, setWpExporting] = useState(false);

  // Durchläufe Tab State
  const [runs, setRuns] = useState<RunEntry[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedRun, setSelectedRun] = useState<RunEntry | null>(null);
  const [logSummary, setLogSummary] = useState<{ fields: LogSummaryField[]; totalContacts: number; totalChanges: number } | null>(null);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [loadingLog, setLoadingLog] = useState(false);
  const [importingRunFile, setImportingRunFile] = useState<number | null>(null);
  const [runImportResult, setRunImportResult] = useState<{ runId: number; totalRows: number; updated: number; notFound: number; unchanged: number; errors: number } | null>(null);

  const loadImports = useCallback(async () => {
    try {
      const res = await authFetch(getIngestUrl('/dialfire/imports'));
      const data = await res.json();
      if (data.success) setImports(data.imports || []);
    } catch { /* ignore */ }
  }, []);

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await authFetch(getIngestUrl('/dialfire/test-connection'));
      const data = await res.json();
      if (data.success) setConnectionStatus(data.connections);
    } catch (err) {
      console.error('Connection test failed:', err);
    }
    setTestingConnection(false);
  };

  const [syncLog, setSyncLog] = useState<Array<{ phase: string; message: string; time?: string }>>([]);

  const startSync = async () => {
    if (syncing || selectedKampagnen.length === 0) return;
    setSyncing(true);
    setSyncResults(null);
    setSyncLog([]);
    try {
      // Job starten
      const startRes = await authFetch(getIngestUrl('/dialfire/sync'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monat, kampagnen: selectedKampagnen }),
      });
      const startData = await startRes.json();
      if (!startData.jobId) throw new Error(startData.error || 'Kein jobId erhalten');
      const jobId = startData.jobId;
      setSyncLog([{ phase: 'pending', message: `Job #${jobId} gestartet...` }]);

      // Status pollen bis fertig
      let lastLogLen = 0;
      const poll = async (): Promise<boolean> => {
        const res = await authFetch(getIngestUrl(`/dialfire/sync/jobs/${jobId}`));
        const job = await res.json();
        const logEntries: any[] = Array.isArray(job.log) ? job.log : [];

        // Neue Log-Einträge anzeigen
        if (logEntries.length > lastLogLen) {
          const newEntries = logEntries.slice(lastLogLen);
          setSyncLog(prev => [...prev, ...newEntries]);
          lastLogLen = logEntries.length;
        }

        // Zwischenergebnisse anzeigen
        if (job.results && Array.isArray(job.results)) {
          setSyncResults(job.results);
        }

        if (job.status === 'done') {
          if (job.results) setSyncResults(job.results);
          loadImports();
          return true;
        }
        if (job.status === 'error') {
          setSyncLog(prev => [...prev, { phase: 'error', message: job.error_message || 'Unbekannter Fehler' }]);
          return true;
        }
        return false;
      };

      // Polling-Loop alle 4s
      await new Promise<void>((resolve) => {
        const interval = setInterval(async () => {
          try {
            const done = await poll();
            if (done) { clearInterval(interval); resolve(); }
          } catch (err) {
            setSyncLog(prev => [...prev, { phase: 'error', message: (err as Error).message }]);
            clearInterval(interval);
            resolve();
          }
        }, 4000);
      });
    } catch (err) {
      alert(`Sync fehlgeschlagen: ${(err as Error).message}`);
    }
    setSyncing(false);
  };

  const loadOptins = useCallback(async () => {
    setLoadingOptins(true);
    setSelectedOptin(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(optinPage));
      params.set('limit', String(optinLimit));
      if (optinFilterKampagne) params.set('kampagne', optinFilterKampagne);
      if (optinFilterMonat) params.set('monat', optinFilterMonat);
      if (optinFilterOptin) params.set('optin', optinFilterOptin);
      if (optinSearch) params.set('search', optinSearch);

      const res = await authFetch(getIngestUrl(`/dialfire/optins?${params.toString()}`));
      const data = await res.json();
      if (data.success) {
        setOptins(data.records || []);
        setOptinTotal(data.total || 0);
      }
    } catch { /* ignore */ }
    setLoadingOptins(false);
  }, [optinPage, optinLimit, optinFilterKampagne, optinFilterMonat, optinFilterOptin, optinSearch]);

  const loadStatistics = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await authFetch(getIngestUrl('/dialfire/statistics'));
      const data = await res.json();
      if (data.success) setStats(data);
    } catch { /* ignore */ }
    setLoadingStats(false);
  }, []);

  const loadWkzStatistik = async () => {
    setLoadingWkz(true);
    try {
      const res = await authFetch(getIngestUrl(`/dialfire/wkz-statistik?von=${wkzVon}&bis=${wkzBis}`));
      const data = await res.json();
      if (data.success) setWkzRows(data.rows || []);
      else alert(`Fehler: ${data.error || data.message}`);
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`);
    }
    setLoadingWkz(false);
  };

  const wkzSum = useMemo(() => {
    if (wkzRows.length === 0) return null;
    const sum = wkzRows.reduce((acc, r) => ({
      total: acc.total + r.total,
      success: acc.success + r.success,
      declined: acc.declined + r.declined,
      optin_ja: acc.optin_ja + r.optin_ja,
      optin_nein: acc.optin_nein + r.optin_nein,
      lose: acc.lose + r.lose,
    }), { total: 0, success: 0, declined: 0, optin_ja: 0, optin_nein: 0, lose: 0 });
    return {
      ...sum,
      succRate: sum.total > 0 ? ((sum.success / sum.total) * 100).toFixed(1) : '0.0',
      jaRate: sum.total > 0 ? ((sum.optin_ja / sum.total) * 100).toFixed(1) : '0.0',
    };
  }, [wkzRows]);

  const sortedWkzRows = useMemo(() => {
    if (wkzRows.length === 0) return [];
    return [...wkzRows].sort((a, b) => {
      let av: number | string, bv: number | string;
      if (wkzSortKey === 'succ_pct') {
        av = a.total > 0 ? a.success / a.total : 0;
        bv = b.total > 0 ? b.success / b.total : 0;
      } else if (wkzSortKey === 'ja_pct') {
        av = a.total > 0 ? a.optin_ja / a.total : 0;
        bv = b.total > 0 ? b.optin_ja / b.total : 0;
      } else if (wkzSortKey === 'erster_tag' || wkzSortKey === 'letzter_tag') {
        av = a[wkzSortKey] || '';
        bv = b[wkzSortKey] || '';
      } else {
        av = a[wkzSortKey];
        bv = b[wkzSortKey];
      }
      if (av < bv) return wkzSortDir === 'asc' ? -1 : 1;
      if (av > bv) return wkzSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [wkzRows, wkzSortKey, wkzSortDir]);

  const handleWkzSort = (key: WkzSortKey) => {
    if (wkzSortKey === key) {
      setWkzSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setWkzSortKey(key);
      setWkzSortDir(key === 'wkz' || key === 'erster_tag' || key === 'letzter_tag' ? 'asc' : 'desc');
    }
  };

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const [importsRes, jobsRes] = await Promise.all([
        authFetch(getIngestUrl('/dialfire/imports')),
        authFetch(getIngestUrl('/dialfire/export-abuntis/jobs')),
      ]);
      const importsData = await importsRes.json();
      const jobsData = await jobsRes.json();

      const entries: RunEntry[] = [];

      // Sync-Imports
      const importsList = importsData.success ? (importsData.imports || []) : (Array.isArray(importsData) ? importsData : []);
      for (const imp of importsList) {
        entries.push({
          type: 'sync',
          id: imp.id,
          date: imp.started_at || imp.created_at,
          label: `Sync ${imp.kampagne_typ} ${imp.telefon_monat}`,
          status: imp.status,
          meta: { kampagne_typ: imp.kampagne_typ, telefon_monat: imp.telefon_monat, total_fetched: imp.total_fetched, total_imported: imp.total_imported, optin_ja: imp.optin_ja, optin_nein: imp.optin_nein, error_message: imp.error_message },
        });
      }

      // Abuntis-Jobs
      const jobsList = Array.isArray(jobsData) ? jobsData : [];
      for (const job of jobsList) {
        entries.push({
          type: 'abuntis',
          id: job.id,
          date: job.created_at,
          label: `Abuntis ${job.monat || ''} ${job.filename || ''}`.trim(),
          status: job.status,
          meta: { monat: job.monat, filename: job.filename, record_count: job.record_count, result_filename: job.result_filename, result_size: job.result_size, error_message: job.error_message, imported_at: job.imported_at },
        });
      }

      // Nach Datum sortieren (absteigend)
      entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setRuns(entries);
    } catch (err) {
      console.error('Failed to load runs:', err);
    }
    setLoadingRuns(false);
  }, []);

  const loadRunDetails = useCallback(async (run: RunEntry) => {
    setSelectedRun(run);
    setLogSummary(null);
    setLogEntries([]);
    setLogTotal(0);
    setLogPage(1);
    setLoadingLog(true);
    try {
      const runType = run.type === 'sync' ? 'sync' : 'abuntis_import';
      const res = await authFetch(getIngestUrl(`/dialfire/processing-log/summary?run_type=${runType}&run_id=${run.id}`));
      const data = await res.json();
      if (data.success) {
        setLogSummary({ fields: data.fields || [], totalContacts: data.totalContacts || 0, totalChanges: data.totalChanges || 0 });
      }
      // Also load first page of details
      const detailRes = await authFetch(getIngestUrl(`/dialfire/processing-log?run_type=${runType}&run_id=${run.id}&page=1&limit=50`));
      const detailData = await detailRes.json();
      if (detailData.success) {
        setLogEntries(detailData.records || []);
        setLogTotal(detailData.total || 0);
      }
    } catch (err) {
      console.error('Failed to load run details:', err);
    }
    setLoadingLog(false);
  }, []);

  const loadLogPage = useCallback(async (page: number) => {
    if (!selectedRun) return;
    setLoadingLog(true);
    setLogPage(page);
    try {
      const runType = selectedRun.type === 'sync' ? 'sync' : 'abuntis_import';
      const res = await authFetch(getIngestUrl(`/dialfire/processing-log?run_type=${runType}&run_id=${selectedRun.id}&page=${page}&limit=50`));
      const data = await res.json();
      if (data.success) {
        setLogEntries(data.records || []);
        setLogTotal(data.total || 0);
      }
    } catch { /* ignore */ }
    setLoadingLog(false);
  }, [selectedRun]);

  useEffect(() => { loadImports(); }, [loadImports]);

  // Abuntis Ergebnisse automatisch laden
  const loadAbuntisResults = useCallback(async () => {
    setLoadingResults(true);
    try {
      const res = await authFetch(getIngestUrl('/dialfire/export-abuntis/results'));
      const data = await res.json();
      if (data.success) setAbuntisFiles(data.files || []);
    } catch { /* ignore */ }
    setLoadingResults(false);
  }, []);
  useEffect(() => { loadAbuntisResults(); }, [loadAbuntisResults]);
  useEffect(() => { if (activeTab === 'optins') loadOptins(); }, [activeTab, loadOptins]);
  useEffect(() => { if (activeTab === 'statistics') loadStatistics(); }, [activeTab, loadStatistics]);
  useEffect(() => { if (activeTab === 'durchlaeufe') loadRuns(); }, [activeTab, loadRuns]);

  // Wpool Datenübernahme: Live-Zähler
  useEffect(() => {
    if (!wpMonat || !/^\d{4}-\d{2}$/.test(wpMonat)) { setWpCount(null); return; }
    let cancelled = false;
    setWpCount(null);
    (async () => {
      try {
        const res = await authFetch(getIngestUrl(`/dialfire/wpool-uebernahme/count?telefon_monat=${wpMonat}&optin_typ=${wpOptinTyp}`));
        const data = await res.json();
        if (!cancelled) setWpCount(res.ok && typeof data.count === 'number' ? data.count : null);
      } catch {
        if (!cancelled) setWpCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [wpMonat, wpOptinTyp]);

  const toggleKampagne = (k: KampagneTyp) => {
    setSelectedKampagnen(prev =>
      prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
    );
  };

  const totalPages = Math.ceil(optinTotal / optinLimit);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <SubPageHeader title="Dialfire OptIn Sync" />

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
        <h1 style={{ color: '#003366', margin: '0 0 1.5rem 0', fontSize: isMobile ? '1.5rem' : '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Phone size={28} weight="fill" /> Dialfire OptIn Sync
        </h1>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
          {([
            { key: 'sync', label: 'Sync & Import' },
            { key: 'optins', label: 'OptIn-Daten' },
            { key: 'statistics', label: 'Statistiken' },
            { key: 'wkz-statistik', label: 'WKZ-Statistik' },
            { key: 'durchlaeufe', label: 'Durchläufe' },
          ] as { key: Tab; label: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderBottom: activeTab === tab.key ? '3px solid #003366' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === tab.key ? '#003366' : '#6b7280',
                fontWeight: activeTab === tab.key ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.95rem',
                marginBottom: '-2px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB: Sync & Import */}
        {activeTab === 'sync' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Connection Test */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#003366', fontSize: '1.1rem' }}>Postgres-Verbindung</h3>
                <button
                  onClick={testConnection}
                  disabled={testingConnection}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: testingConnection ? '#9ca3af' : '#003366',
                    color: 'white', border: 'none', borderRadius: '6px', cursor: testingConnection ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <ArrowsClockwise size={16} weight={testingConnection ? 'regular' : 'bold'} />
                  {testingConnection ? 'Teste...' : 'Verbindung testen'}
                </button>
              </div>
              {connectionStatus && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '0.75rem' }}>
                  {Object.entries(connectionStatus).map(([typ, info]) => (
                    <div key={typ} style={{
                      padding: '0.75rem', borderRadius: '6px',
                      backgroundColor: info.connected ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${info.connected ? '#bbf7d0' : '#fecaca'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        {info.connected ? <Check size={16} color="#16a34a" weight="bold" /> : <X size={16} color="#dc2626" weight="bold" />}
                        <strong style={{ color: KAMPAGNE_COLORS[typ as KampagneTyp] }}>{typ}</strong>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{KAMPAGNE_LABELS[typ as KampagneTyp]}</span>
                      </div>
                      {info.connected && info.recordCount !== undefined && (
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{info.recordCount.toLocaleString()} Contacts</div>
                      )}
                      {info.error && <div style={{ fontSize: '0.8rem', color: '#dc2626' }}>{info.error.slice(0, 80)}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sync Panel */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>Sync starten</h3>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', alignItems: isMobile ? 'stretch' : 'flex-end' }}>
                <div>
                  <TextField
                    type="month"
                    label="Telefonie-Monat"
                    value={monat}
                    onChange={e => setMonat(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem', fontWeight: 500 }}>Kampagnen</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {(['CB', 'IB', 'KR', 'UG'] as KampagneTyp[]).map(k => (
                      <button
                        key={k}
                        onClick={() => toggleKampagne(k)}
                        style={{
                          padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer',
                          border: `2px solid ${KAMPAGNE_COLORS[k]}`,
                          backgroundColor: selectedKampagnen.includes(k) ? KAMPAGNE_COLORS[k] : 'white',
                          color: selectedKampagnen.includes(k) ? 'white' : KAMPAGNE_COLORS[k],
                          fontWeight: 600,
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={startSync}
                  disabled={syncing || selectedKampagnen.length === 0}
                  style={{
                    padding: '0.5rem 1.5rem', backgroundColor: syncing ? '#9ca3af' : '#059669',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: syncing ? 'not-allowed' : 'pointer', fontSize: '0.95rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap',
                  }}
                >
                  <ArrowsClockwise size={18} weight="bold" className={syncing ? 'spin' : ''} />
                  {syncing ? 'Sync läuft...' : 'Sync starten'}
                </button>
              </div>

              {/* Sync Log */}
              {syncLog.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', maxHeight: '200px', overflowY: 'auto' }}>
                  {syncLog.map((entry, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', color: entry.phase === 'error' || entry.phase === 'error_kampagne' ? '#dc2626' : entry.phase === 'done' || entry.phase === 'done_kampagne' ? '#059669' : '#374151', padding: '0.15rem 0', fontFamily: 'monospace' }}>
                      {entry.time ? new Date(entry.time).toLocaleTimeString() + ' ' : ''}{entry.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Sync Results */}
              {syncResults && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#374151' }}>Ergebnis</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '0.75rem' }}>
                    {syncResults.map(r => (
                      <div key={r.kampagne} style={{
                        padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb',
                        backgroundColor: r.error ? '#fef2f2' : '#f0fdf4',
                      }}>
                        <div style={{ fontWeight: 600, color: KAMPAGNE_COLORS[r.kampagne], marginBottom: '0.5rem', fontSize: '1rem' }}>
                          {r.kampagne} - {KAMPAGNE_LABELS[r.kampagne]}
                        </div>
                        {r.error ? (
                          <div style={{ color: '#dc2626', fontSize: '0.85rem' }}>{r.error}</div>
                        ) : (
                          <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                            <div>Gelesen: <strong>{r.totalFetched.toLocaleString()}</strong></div>
                            <div>Importiert: <strong>{r.totalImported.toLocaleString()}</strong></div>
                            <div style={{ color: '#059669' }}>OptIn Ja: <strong>{r.optinJa.toLocaleString()}</strong></div>
                            <div style={{ color: '#dc2626' }}>OptIn Nein: <strong>{r.optinNein.toLocaleString()}</strong></div>
                            <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>{(r.durationMs / 1000).toFixed(1)}s</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                </div>
              )}
            </div>

            {/* Abuntis CSV Export */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>Abuntis Adressprüfung (ta.arsrv.de)</h3>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                  <TextField
                    type="month"
                    label="Monat"
                    value={monat}
                    onChange={e => setMonat(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <button
                  onClick={async () => {
                    setExporting(true);
                    try {
                      const params = new URLSearchParams();
                      params.set('monat', monat);
                      const res = await authFetch(getIngestUrl(`/dialfire/export-abuntis?${params.toString()}`));
                      if (!res.ok) {
                        const errData = await res.json();
                        throw new Error(errData.error || errData.message || 'Download fehlgeschlagen');
                      }
                      const blob = await res.blob();
                      const disposition = res.headers.get('Content-Disposition') || '';
                      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
                      const filename = filenameMatch ? filenameMatch[1] : `1191_export.csv`;
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      alert(`Export fehlgeschlagen: ${(err as Error).message}`);
                    }
                    setExporting(false);
                  }}
                  disabled={exporting}
                  style={{
                    padding: '0.5rem 1.25rem',
                    backgroundColor: exporting ? '#9ca3af' : '#059669',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: '0.9rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap',
                  }}
                >
                  <Export size={18} /> {exporting ? 'Exportiere...' : 'CSV herunterladen'}
                </button>
                <button
                  onClick={async () => {
                    setSending(true);
                    setSendResult(null);
                    setPollStatus([]);
                    setPollResultFile(null);
                    try {
                      // Job starten
                      const startRes = await authFetch(getIngestUrl(`/dialfire/export-abuntis/send-and-poll?monat=${monat}&initialDelay=300&interval=60`), { method: 'POST' });
                      const startData = await startRes.json();
                      if (!startData.jobId) throw new Error(startData.error || 'Kein jobId erhalten');
                      const jobId = startData.jobId;
                      setPollStatus([{ phase: 'pending', message: `Job #${jobId} gestartet...` }]);

                      // Status pollen bis fertig
                      let lastLogLen = 0;
                      const poll = async (): Promise<boolean> => {
                        const res = await authFetch(getIngestUrl(`/dialfire/export-abuntis/jobs/${jobId}`));
                        const job = await res.json();
                        const logEntries: any[] = Array.isArray(job.log) ? job.log : [];

                        // Neue Log-Einträge anzeigen
                        if (logEntries.length > lastLogLen) {
                          const newEntries = logEntries.slice(lastLogLen);
                          setPollStatus(prev => [...prev, ...newEntries]);
                          lastLogLen = logEntries.length;

                          // Send-Result extrahieren
                          for (const e of newEntries) {
                            if (e.phase === 'sent' && job.filename) {
                              setSendResult({ filename: job.filename, recordCount: job.record_count, orderId: job.order_id });
                            }
                          }
                        }

                        // Ergebnis prüfen
                        if (job.status === 'done' && job.result_filename) {
                          setPollResultFile({ name: job.result_filename, size: job.result_size });
                          return true;
                        }
                        if (job.status === 'error' || job.status === 'timeout') {
                          return true;
                        }
                        return false;
                      };

                      // Polling-Loop
                      await new Promise<void>((resolve) => {
                        const interval = setInterval(async () => {
                          try {
                            const done = await poll();
                            if (done) { clearInterval(interval); resolve(); }
                          } catch (err) {
                            setPollStatus(prev => [...prev, { phase: 'error', message: (err as Error).message }]);
                            clearInterval(interval);
                            resolve();
                          }
                        }, 4000);
                      });
                    } catch (err) {
                      setPollStatus(prev => [...prev, { phase: 'error', message: (err as Error).message }]);
                    }
                    setSending(false);
                  }}
                  disabled={sending}
                  style={{
                    padding: '0.5rem 1.25rem',
                    backgroundColor: sending ? '#9ca3af' : '#2563eb',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: '0.9rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap',
                  }}
                >
                  <ArrowsClockwise size={18} weight={sending ? 'regular' : 'bold'} />
                  {sending ? 'Sende & warte...' : 'Senden + Ergebnis abholen'}
                </button>
              </div>
              {/* Send Result */}
              {sendResult && (
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '0.75rem',
                  backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '0.85rem',
                }}>
                  <div style={{ color: '#166534', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Gesendet: {sendResult.filename} ({sendResult.recordCount.toLocaleString()} Records)
                  </div>
                  {sendResult.orderId && (
                    <div style={{ color: '#166534' }}>Auftrags-ID: {sendResult.orderId}</div>
                  )}
                </div>
              )}
              {/* Poll Result */}
              {pollResultFile && (
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '0.75rem',
                  backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '0.85rem',
                }}>
                  <div style={{ color: '#1e40af', fontWeight: 600 }}>
                    Ergebnis: {pollResultFile.name} ({(pollResultFile.size / 1024).toFixed(1)} KB)
                  </div>
                </div>
              )}
              {/* Poll Status Log */}
              {pollStatus.length > 0 && (
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '0.75rem',
                  backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.8rem',
                  maxHeight: '200px', overflowY: 'auto',
                }}>
                  {pollStatus.map((s, i) => (
                    <div key={i} style={{
                      padding: '0.2rem 0', color:
                        s.phase === 'error' ? '#dc2626' :
                        s.phase === 'done' || s.phase === 'complete' ? '#059669' :
                        s.phase === 'sent' ? '#166534' :
                        '#6b7280',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#9ca3af', minWidth: '60px' }}>
                        {s.phase}
                      </span>
                      {s.message}
                      {sending && i === pollStatus.length - 1 && s.phase === 'waiting' && (
                        <ArrowsClockwise size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                Sendet CSV an ta.arsrv.de (SFTP), wartet auf Verarbeitung, holt Ergebnis automatisch ab.
              </div>
            </div>

            {/* Abuntis Ergebnisse */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#003366', fontSize: '1.1rem' }}>Abuntis Ergebnisse (ta.arsrv.de)</h3>
                <button
                  onClick={() => loadAbuntisResults()}
                  disabled={loadingResults}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: loadingResults ? '#9ca3af' : '#003366',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: loadingResults ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <ArrowsClockwise size={16} weight={loadingResults ? 'regular' : 'bold'} />
                  {loadingResults ? 'Lade...' : 'Aktualisieren'}
                </button>
              </div>
              {abuntisFiles.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Datei</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#374151' }}>Größe</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Datum</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151' }}>Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abuntisFiles.map(f => (
                        <tr key={f.name} style={{ borderBottom: '1px solid #f3f4f6', opacity: f.status === 'waiting' ? 0.6 : 1 }}>
                          <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {f.name}
                            {f.staged && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '3px', background: '#dbeafe', color: '#1d4ed8' }}>bereit</span>}
                            {f.status === 'waiting' && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '3px', background: '#fef3c7', color: '#92400e' }}>Warte auf Fertigstellung</span>}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                            {f.status === 'waiting' ? '—' : `${(f.size / 1024).toFixed(1)} KB`}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                            {new Date(f.modifyTime).toLocaleString('de-DE')}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                            {f.status === 'waiting' ? (
                              <span style={{ fontSize: '0.8rem', color: '#92400e', fontStyle: 'italic' }}>Verarbeitung läuft...</span>
                            ) : (
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await authFetch(getIngestUrl(`/dialfire/export-abuntis/results/${encodeURIComponent(f.name)}`));
                                    if (!res.ok) throw new Error('Download fehlgeschlagen');
                                    const blob = await res.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = f.name;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (err) {
                                    alert(`Download fehlgeschlagen: ${(err as Error).message}`);
                                  }
                                }}
                                style={{
                                  padding: '0.3rem 0.75rem', backgroundColor: '#059669', color: 'white',
                                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                                }}
                              >
                                Download
                              </button>
                              <button
                                onClick={async () => {
                                  setImportingFile(f.name);
                                  setImportResult(null);
                                  try {
                                    const res = await authFetch(getIngestUrl(`/dialfire/export-abuntis/results/${encodeURIComponent(f.name)}/stage`), {
                                      method: 'POST',
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      navigate('/abuntis-review/' + data.jobId);
                                    } else {
                                      alert(`Staging-Fehler: ${data.error || data.message}`);
                                    }
                                  } catch (err) {
                                    alert(`Import fehlgeschlagen: ${(err as Error).message}`);
                                  }
                                  setImportingFile(null);
                                }}
                                disabled={importingFile === f.name}
                                style={{
                                  padding: '0.3rem 0.75rem',
                                  backgroundColor: importingFile === f.name ? '#9ca3af' : '#2563eb',
                                  color: 'white',
                                  border: 'none', borderRadius: '4px',
                                  cursor: importingFile === f.name ? 'not-allowed' : 'pointer',
                                  fontSize: '0.8rem',
                                }}
                              >
                                {importingFile === f.name ? 'Lade...' : 'Prüfen & Importieren'}
                              </button>
                            </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Keine Ergebnisse der letzten 6 Wochen vorhanden
                </div>
              )}
              {importResult && (
                <div style={{
                  marginTop: '1rem', padding: '1rem', borderRadius: '6px',
                  backgroundColor: importResult.errors > 0 ? '#fffbeb' : '#f0fdf4',
                  border: `1px solid ${importResult.errors > 0 ? '#fde68a' : '#bbf7d0'}`,
                  fontSize: '0.85rem',
                }}>
                  <div style={{ fontWeight: 600, color: '#166534', marginBottom: '0.5rem' }}>
                    Import-Ergebnis: {importResult.filename}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
                    <div>Gesamt: <strong>{importResult.totalRows}</strong></div>
                    <div style={{ color: '#059669' }}>Aktualisiert: <strong>{importResult.updated}</strong></div>
                    <div style={{ color: '#6b7280' }}>Unverändert: <strong>{importResult.unchanged}</strong></div>
                    <div style={{ color: '#d97706' }}>Nicht gefunden: <strong>{importResult.notFound}</strong></div>
                    {importResult.errors > 0 && <div style={{ color: '#dc2626' }}>Fehler: <strong>{importResult.errors}</strong></div>}
                    <div style={{ color: '#6b7280' }}>Dauer: {(importResult.durationMs / 1000).toFixed(1)}s</div>
                  </div>
                </div>
              )}
            </div>

            {/* Wpool Datenübernahme */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>Wpool Datenübernahme</h3>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <TextField
                  type="month"
                  label="Telefonmonat"
                  value={wpMonat}
                  onChange={e => setWpMonat(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl component="fieldset">
                  <FormLabel component="legend" style={{ fontSize: '0.8rem' }}>OptIn-Typ</FormLabel>
                  <RadioGroup
                    row
                    value={wpOptinTyp}
                    onChange={e => setWpOptinTyp(e.target.value as 'generell' | 'brief' | 'telefon' | 'email')}
                  >
                    <FormControlLabel value="generell" control={<Radio size="small" />} label="Generell" />
                    <FormControlLabel value="brief" control={<Radio size="small" />} label="Brief" />
                    <FormControlLabel value="telefon" control={<Radio size="small" />} label="Telefon" />
                    <FormControlLabel value="email" control={<Radio size="small" />} label="E-Mail" />
                  </RadioGroup>
                </FormControl>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <TextField label="hkn" value={wpHkn} onChange={e => setWpHkn(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                <TextField label="lfr" value={wpLfr} onChange={e => setWpLfr(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                <TextField label="inf2" value={wpInf2} onChange={e => setWpInf2(e.target.value)} size="small" InputLabelProps={{ shrink: true }} style={{ minWidth: '220px' }} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ color: '#374151', fontWeight: 600 }}>
                  {wpCount !== null ? `${wpCount} Datensätze` : '…'}
                </div>
                <button
                  onClick={async () => {
                    setWpExporting(true);
                    try {
                      const params = new URLSearchParams();
                      params.set('telefon_monat', wpMonat);
                      params.set('optin_typ', wpOptinTyp);
                      params.set('hkn', wpHkn);
                      params.set('lfr', wpLfr);
                      params.set('inf2', wpInf2);
                      const res = await authFetch(getIngestUrl(`/dialfire/wpool-uebernahme/export?${params.toString()}`));
                      if (!res.ok) {
                        const errData = await res.json();
                        throw new Error(errData.error || errData.message || 'Download fehlgeschlagen');
                      }
                      const blob = await res.blob();
                      const disposition = res.headers.get('Content-Disposition') || '';
                      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
                      const filename = filenameMatch ? filenameMatch[1] : 'wpool_uebernahme.csv';
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      alert(`Export fehlgeschlagen: ${(err as Error).message}`);
                    }
                    setWpExporting(false);
                  }}
                  disabled={wpExporting}
                  style={{
                    padding: '0.5rem 1.25rem',
                    backgroundColor: wpExporting ? '#9ca3af' : '#059669',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: wpExporting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <Export size={18} />
                  {wpExporting ? 'Erzeuge…' : 'CSV herunterladen'}
                </button>
              </div>
            </div>

            {/* Import-Historie */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>Import-Historie</h3>
              {imports.length === 0 ? (
                <div style={{ color: '#6b7280', fontStyle: 'italic' }}>Noch keine Importe vorhanden</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Kampagne</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Monat</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#374151' }}>Gelesen</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#374151' }}>Importiert</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>Ja</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>Nein</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {imports.map(imp => (
                        <tr key={imp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.5rem 0.75rem' }}>
                            <span style={{ fontWeight: 600, color: KAMPAGNE_COLORS[imp.kampagne_typ] }}>{imp.kampagne_typ}</span>
                            {' '}<span style={{ color: '#6b7280' }}>{KAMPAGNE_LABELS[imp.kampagne_typ]}</span>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{imp.telefon_monat}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{imp.total_fetched.toLocaleString()}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{imp.total_imported.toLocaleString()}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#059669' }}>{imp.optin_ja.toLocaleString()}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#dc2626' }}>{imp.optin_nein.toLocaleString()}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500,
                              backgroundColor: imp.status === 'completed' ? '#dcfce7' : imp.status === 'running' ? '#fef3c7' : '#fecaca',
                              color: imp.status === 'completed' ? '#166534' : imp.status === 'running' ? '#92400e' : '#991b1b',
                            }}>
                              {imp.status}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                            {formatDateTime(imp.started_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: OptIn-Daten */}
        {activeTab === 'optins' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Filter */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Kampagne</label>
                  <select
                    value={optinFilterKampagne}
                    onChange={e => { setOptinFilterKampagne(e.target.value); setOptinPage(1); }}
                    style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <option value="">Alle</option>
                    <option value="CB">CB - Callbacks</option>
                    <option value="IB">IB - Inbound</option>
                    <option value="KR">KR - KüRü</option>
                    <option value="UG">UG - Upgrader</option>
                  </select>
                </div>
                <div>
                  <TextField
                    type="month"
                    label="Monat"
                    value={optinFilterMonat}
                    onChange={e => { setOptinFilterMonat(e.target.value); setOptinPage(1); }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>OptIn</label>
                  <select
                    value={optinFilterOptin}
                    onChange={e => { setOptinFilterOptin(e.target.value); setOptinPage(1); }}
                    style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <option value="">Alle</option>
                    <option value="Ja">Ja</option>
                    <option value="Nein">Nein</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Suche</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Name, PLZ, WKZ, Contact-ID..."
                      value={optinSearch}
                      onChange={e => setOptinSearch(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { setOptinPage(1); loadOptins(); } }}
                      style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                    />
                    <button
                      onClick={() => { setOptinPage(1); loadOptins(); }}
                      style={{ padding: '0.5rem 0.75rem', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      <MagnifyingGlass size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabelle */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    {loadingOptins ? 'Lade...' : `${optinTotal.toLocaleString()} Ergebnisse`}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (optinFilterKampagne) params.set('kampagne', optinFilterKampagne);
                        if (optinFilterMonat) params.set('monat', optinFilterMonat);
                        if (optinFilterOptin) params.set('optin', optinFilterOptin);
                        if (optinSearch) params.set('search', optinSearch);
                        const res = await authFetch(getIngestUrl(`/dialfire/optins/export-csv?${params.toString()}`));
                        if (!res.ok) throw new Error('Export fehlgeschlagen');
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'optin_cache.csv';
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) { console.error('CSV export error:', err); }
                    }}
                    style={{
                      padding: '0.3rem 0.75rem', backgroundColor: '#003366',
                      color: 'white', border: 'none', borderRadius: '4px',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}
                  >
                    <Export size={14} weight="bold" />
                    CSV Export
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => setOptinPage(p => Math.max(1, p - 1))}
                    disabled={optinPage <= 1}
                    style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: optinPage <= 1 ? 'not-allowed' : 'pointer', opacity: optinPage <= 1 ? 0.5 : 1 }}
                  >
                    <CaretLeft size={16} />
                  </button>
                  <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {optinPage} / {totalPages || 1}</span>
                  <button
                    onClick={() => setOptinPage(p => Math.min(totalPages, p + 1))}
                    disabled={optinPage >= totalPages}
                    style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: optinPage >= totalPages ? 'not-allowed' : 'pointer', opacity: optinPage >= totalPages ? 0.5 : 1 }}
                  >
                    <CaretRight size={16} />
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Kamp.</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Monat</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>OptIn</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>PLZ/Ort</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>WKZ</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>FC Task</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>FC Status</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>FC Datum</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Agent</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Rec.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optins.map(r => (
                      <React.Fragment key={r.id}>
                      <tr onClick={() => setSelectedOptin(selectedOptin?.id === r.id ? null : r)} style={{ borderBottom: selectedOptin?.id === r.id ? 'none' : '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: selectedOptin?.id === r.id ? '#eff6ff' : undefined }} onMouseOver={e => { if (selectedOptin?.id !== r.id) (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#f9fafb'; }} onMouseOut={e => { if (selectedOptin?.id !== r.id) (e.currentTarget as HTMLTableRowElement).style.backgroundColor = ''; }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <span style={{ fontWeight: 600, color: KAMPAGNE_COLORS[r.kampagne_typ] }}>{r.kampagne_typ}</span>
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.telefon_monat}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                          <span style={{
                            padding: '1px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                            backgroundColor: r.optin === 'Ja' ? '#dcfce7' : '#fecaca',
                            color: r.optin === 'Ja' ? '#166534' : '#991b1b',
                          }}>
                            {r.optin}
                          </span>
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{r.vnm} {r.nnm}</td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.plz} {r.ort}</td>
                        <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.wkz || '-'}</td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.fc_task || '-'}</td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: '4px', fontSize: '0.75rem',
                            backgroundColor: r.fc_status === 'success' ? '#dcfce7' : r.fc_status === 'declined' ? '#fee2e2' : '#f3f4f6',
                            color: r.fc_status === 'success' ? '#166534' : r.fc_status === 'declined' ? '#991b1b' : '#374151',
                          }}>
                            {r.fc_status || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatDate(r.fc_date)}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.fc_user || '-'}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                          {r.recording_url ? (
                            <a href={r.recording_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }} title="Aufzeichnung anhören" onClick={e => e.stopPropagation()}>
                              <Export size={14} />
                            </a>
                          ) : '-'}
                        </td>
                      </tr>
                      {selectedOptin?.id === r.id && (
                        <tr>
                          <td colSpan={11} style={{ padding: 0, borderBottom: '1px solid #f3f4f6' }}>
                            <div style={{ padding: '1rem 1.25rem', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <h4 style={{ margin: 0, color: '#003366', fontSize: '0.95rem' }}>
                                  {r.vnm} {r.nnm} — {r.kampagne_typ} {r.telefon_monat}
                                </h4>
                                <button onClick={(e) => { e.stopPropagation(); setSelectedOptin(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.2rem' }}>
                                  <X size={16} />
                                </button>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem', fontSize: '0.82rem' }}>
                                {/* Personendaten */}
                                <div>
                                  <div style={{ fontWeight: 600, color: '#003366', marginBottom: '0.5rem', borderBottom: '1px solid #cbd5e1', paddingBottom: '0.25rem' }}>Personendaten</div>
                                  {([
                                    ['ID', r.id],
                                    ['Contact-ID', r.contact_id],
                                    ['Anrede', r.anrede],
                                    ['Titel', r.titel],
                                    ['Vorname', r.vnm],
                                    ['Nachname', r.nnm],
                                    ['Straße', r.str],
                                    ['PLZ', r.plz],
                                    ['Ort', r.ort],
                                    ['LKN', r.lkn],
                                    ['Geburtsdatum', r.geburtsdatum ? formatDate(r.geburtsdatum) : ''],
                                    ['Email', r.email],
                                    ['$phone', r.phone],
                                    ['Telefon (TNS)', r.telefon],
                                    ['Handy (HNS)', r.handy],
                                    ['AID', r.aid],
                                  ] as [string, any][]).map(([label, val]) => (
                                    <div key={label} style={{ display: 'flex', padding: '0.15rem 0' }}>
                                      <span style={{ color: '#6b7280', minWidth: '100px' }}>{label}:</span>
                                      <span style={{ color: '#1e293b', wordBreak: 'break-all' }}>{val || '–'}</span>
                                    </div>
                                  ))}
                                </div>
                                {/* OptIn & Kampagne */}
                                <div>
                                  <div style={{ fontWeight: 600, color: '#003366', marginBottom: '0.5rem', borderBottom: '1px solid #cbd5e1', paddingBottom: '0.25rem' }}>OptIn & Kampagne</div>
                                  {([
                                    ['OptIn', r.optin],
                                    ['OptIn Telefon', r.optin_telefon],
                                    ['OptIn Email', r.optin_email],
                                    ['OptIn Brief', r.optin_brief],
                                    ['Kampagne', r.kampagne_typ],
                                    ['Telefon-Monat', r.telefon_monat],
                                    ['WKZ', r.wkz],
                                    ['Campaign-ID', r.campaign_id],
                                    ['Contact Status', r.contact_status],
                                    ['MuB PLZ', r.mub_plz],
                                    ['MuB Ort', r.mub_ort],
                                  ] as [string, any][]).map(([label, val]) => (
                                    <div key={label} style={{ display: 'flex', padding: '0.15rem 0' }}>
                                      <span style={{ color: '#6b7280', minWidth: '110px' }}>{label}:</span>
                                      {label.startsWith('OptIn') && val ? (
                                        <span style={{
                                          padding: '0 6px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                                          backgroundColor: val === 'Ja' ? '#dcfce7' : val === 'Nein' ? '#fecaca' : '#f3f4f6',
                                          color: val === 'Ja' ? '#166534' : val === 'Nein' ? '#991b1b' : '#374151',
                                        }}>{val}</span>
                                      ) : (
                                        <span style={{ color: '#1e293b', wordBreak: 'break-all' }}>{val || '–'}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {/* Dialfire & Sync */}
                                <div>
                                  <div style={{ fontWeight: 600, color: '#003366', marginBottom: '0.5rem', borderBottom: '1px solid #cbd5e1', paddingBottom: '0.25rem' }}>Dialfire & Sync</div>
                                  {([
                                    ['FC Task', r.fc_task],
                                    ['FC Status', r.fc_status],
                                    ['FC Status Detail', r.fc_status_detail],
                                    ['FC Datum', r.fc_date ? formatDate(r.fc_date) : ''],
                                    ['FC Agent', r.fc_user],
                                    ['Recording URL', r.recording_url],
                                    ['OptIn Filename', r.optin_filename],
                                    ['Optinort Alt', r.optinort_alt],
                                    ['Letzter Sync', r.last_synced_at ? formatDate(r.last_synced_at) : ''],
                                  ] as [string, any][]).map(([label, val]) => (
                                    <div key={label} style={{ display: 'flex', padding: '0.15rem 0' }}>
                                      <span style={{ color: '#6b7280', minWidth: '110px' }}>{label}:</span>
                                      {(label === 'Recording URL' || label === 'OptIn Filename') && val ? (
                                        <a href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all', fontSize: '0.78rem' }} onClick={e => e.stopPropagation()}>{val}</a>
                                      ) : (
                                        <span style={{ color: '#1e293b', wordBreak: 'break-all' }}>{val || '–'}</span>
                                      )}
                                    </div>
                                  ))}
                                  {r.processing_errors && (
                                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                                      <span style={{ color: '#991b1b', fontWeight: 600, fontSize: '0.78rem' }}>Fehler: </span>
                                      <span style={{ color: '#991b1b', fontSize: '0.78rem' }}>{r.processing_errors}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                    {optins.length === 0 && !loadingOptins && (
                      <tr><td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>Keine Daten gefunden</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB: Statistiken */}
        {activeTab === 'statistics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {loadingStats ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Lade Statistiken...</div>
            ) : stats ? (
              <>
                {/* Zusammenfassung pro Kampagne */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                  {(stats.perKampagne as any[] || []).map((k: any) => (
                    <div key={k.kampagne_typ} style={{
                      backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: `2px solid ${KAMPAGNE_COLORS[k.kampagne_typ as KampagneTyp]}20`,
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>{KAMPAGNE_LABELS[k.kampagne_typ as KampagneTyp]}</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: KAMPAGNE_COLORS[k.kampagne_typ as KampagneTyp] }}>
                        {Number(k.total).toLocaleString()}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                        <span style={{ color: '#059669' }}>Ja: {Number(k.optin_ja).toLocaleString()}</span>
                        <span style={{ color: '#dc2626' }}>Nein: {Number(k.optin_nein).toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{k.monate} Monate</div>
                    </div>
                  ))}
                  {(stats.perKampagne as any[] || []).length === 0 && (
                    <div style={{ gridColumn: '1/-1', padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                      Noch keine Daten vorhanden. Starten Sie zuerst einen Sync.
                    </div>
                  )}
                </div>

                {/* Monats-Tabelle */}
                {(stats.perMonat as any[] || []).length > 0 && (
                  <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>Aufschlüsselung nach Monat</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Monat</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Kampagne</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Gesamt</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>OptIn Ja</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>OptIn Nein</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Quote</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(stats.perMonat as any[]).map((row: any, idx: number) => {
                            const quote = row.total > 0 ? ((Number(row.optin_ja) / Number(row.total)) * 100).toFixed(1) : '0.0';
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{row.telefon_monat}</td>
                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                  <span style={{ fontWeight: 600, color: KAMPAGNE_COLORS[row.kampagne_typ as KampagneTyp] }}>{row.kampagne_typ}</span>
                                  {' '}<span style={{ color: '#6b7280' }}>{KAMPAGNE_LABELS[row.kampagne_typ as KampagneTyp]}</span>
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{Number(row.total).toLocaleString()}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#059669' }}>{Number(row.optin_ja).toLocaleString()}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#dc2626' }}>{Number(row.optin_nein).toLocaleString()}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 500 }}>{quote}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Keine Statistiken verfügbar</div>
            )}
          </div>
        )}

        {/* TAB: WKZ-Statistik */}
        {activeTab === 'wkz-statistik' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Filter */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.1rem' }}>WKZ-Statistik (IB - Inbound)</h3>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', alignItems: isMobile ? 'stretch' : 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem', fontWeight: 500 }}>Von</label>
                  <input
                    type="date"
                    value={wkzVon}
                    onChange={e => setWkzVon(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem', fontWeight: 500 }}>Bis</label>
                  <input
                    type="date"
                    value={wkzBis}
                    onChange={e => setWkzBis(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                </div>
                <button
                  onClick={loadWkzStatistik}
                  disabled={loadingWkz}
                  style={{
                    padding: '0.5rem 1.5rem', backgroundColor: loadingWkz ? '#9ca3af' : '#059669',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: loadingWkz ? 'not-allowed' : 'pointer', fontSize: '0.95rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap',
                  }}
                >
                  <MagnifyingGlass size={18} weight="bold" />
                  {loadingWkz ? 'Lade...' : 'Statistik laden'}
                </button>
              </div>
            </div>

            {/* Ergebnis-Tabelle */}
            {wkzRows.length > 0 && (
              <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#003366', fontSize: '1.1rem' }}>Ergebnis: {wkzVon} bis {wkzBis}</h3>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{wkzRows.length} WKZ</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        {([
                          { key: 'wkz', label: 'WKZ', align: 'left' as const },
                          { key: 'total', label: 'Gesamt', align: 'right' as const },
                          { key: 'success', label: 'Success', align: 'right' as const },
                          { key: 'succ_pct', label: 'Succ %', align: 'right' as const },
                          { key: 'declined', label: 'Declined', align: 'right' as const },
                          { key: 'optin_ja', label: 'OptIn Ja', align: 'right' as const, color: '#059669' },
                          { key: 'optin_nein', label: 'OptIn Nein', align: 'right' as const, color: '#dc2626' },
                          { key: 'ja_pct', label: 'Ja %', align: 'right' as const },
                          { key: 'lose', label: 'Lose', align: 'right' as const },
                          { key: 'agents', label: 'Agents', align: 'right' as const },
                          { key: 'erster_tag', label: 'Von', align: 'left' as const },
                          { key: 'letzter_tag', label: 'Bis', align: 'left' as const },
                        ] as { key: WkzSortKey; label: string; align: 'left' | 'right'; color?: string }[]).map(col => (
                          <th
                            key={col.key}
                            onClick={() => handleWkzSort(col.key)}
                            style={{
                              padding: '0.75rem', textAlign: col.align, color: col.color || '#374151',
                              cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                            }}
                          >
                            {col.label}
                            {wkzSortKey === col.key && (
                              wkzSortDir === 'asc'
                                ? <CaretUp size={12} weight="bold" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                                : <CaretDown size={12} weight="bold" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    {wkzSum && (
                      <tbody>
                        <tr style={{ backgroundColor: '#f0f4f8', fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>
                          <td style={{ padding: '0.6rem 0.75rem' }}>SUMME</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.total.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.success.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.succRate}%</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.declined.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#059669' }}>{wkzSum.optin_ja.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#dc2626' }}>{wkzSum.optin_nein.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.jaRate}%</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.lose.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: '0.6rem 0.75rem' }} colSpan={3}></td>
                        </tr>
                      </tbody>
                    )}
                    <tbody>
                      {sortedWkzRows.map((row, idx) => {
                        const succRate = row.total > 0 ? ((row.success / row.total) * 100).toFixed(1) : '0.0';
                        const jaRate = row.total > 0 ? ((row.optin_ja / row.total) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontWeight: 500 }}>{row.wkz}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{row.total.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{row.success.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{succRate}%</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{row.declined.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#059669', fontWeight: 500 }}>{row.optin_ja.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#dc2626', fontWeight: 500 }}>{row.optin_nein.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 500 }}>{jaRate}%</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{row.lose.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{row.agents}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{row.erster_tag || '-'}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{row.letzter_tag || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {wkzSum && (
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f0f4f8', fontWeight: 600 }}>
                          <td style={{ padding: '0.6rem 0.75rem' }}>SUMME</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.total.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.success.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.succRate}%</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.declined.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#059669' }}>{wkzSum.optin_ja.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#dc2626' }}>{wkzSum.optin_nein.toLocaleString()}</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.jaRate}%</td>
                          <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{wkzSum.lose.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: '0.6rem 0.75rem' }} colSpan={3}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        {/* TAB: Durchläufe */}
        {activeTab === 'durchlaeufe' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Runs-Liste */}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#003366', fontSize: '1.1rem' }}>Verarbeitungs-Durchläufe</h3>
                <button
                  onClick={loadRuns}
                  disabled={loadingRuns}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: loadingRuns ? '#9ca3af' : '#003366',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: loadingRuns ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <ArrowsClockwise size={16} weight={loadingRuns ? 'regular' : 'bold'} />
                  {loadingRuns ? 'Lade...' : 'Aktualisieren'}
                </button>
              </div>

              {runs.length === 0 && !loadingRuns ? (
                <div style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>Keine Durchläufe vorhanden</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Typ</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Beschreibung</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#374151' }}>Kennzahlen</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151' }}>Datum</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151' }}>Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map(run => (
                        <tr
                          key={`${run.type}-${run.id}`}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: selectedRun?.type === run.type && selectedRun?.id === run.id ? '#eff6ff' : 'transparent',
                            cursor: 'pointer',
                          }}
                          onClick={() => loadRunDetails(run)}
                        >
                          <td style={{ padding: '0.5rem 0.75rem' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                              backgroundColor: run.type === 'sync' ? '#dbeafe' : '#fce7f3',
                              color: run.type === 'sync' ? '#1e40af' : '#9d174d',
                            }}>
                              {run.type === 'sync' ? 'Sync' : 'Abuntis'}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{run.label}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500,
                              backgroundColor: run.status === 'completed' || run.status === 'done' ? '#dcfce7' :
                                run.status === 'running' || run.status === 'sending' || run.status === 'polling' || run.status === 'waiting' ? '#fef3c7' :
                                run.status === 'error' || run.status === 'failed' ? '#fecaca' : '#f3f4f6',
                              color: run.status === 'completed' || run.status === 'done' ? '#166534' :
                                run.status === 'running' || run.status === 'sending' || run.status === 'polling' || run.status === 'waiting' ? '#92400e' :
                                run.status === 'error' || run.status === 'failed' ? '#991b1b' : '#374151',
                            }}>
                              {run.status}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#6b7280' }}>
                            {run.type === 'sync' && run.meta.total_imported != null && (
                              <span>{Number(run.meta.total_imported).toLocaleString()} imp. | <span style={{ color: '#059669' }}>{Number(run.meta.optin_ja || 0).toLocaleString()} Ja</span></span>
                            )}
                            {run.type === 'abuntis' && run.meta.record_count != null && (
                              <span>{Number(run.meta.record_count).toLocaleString()} Records</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                            {formatDateTime(run.date)}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                            {run.type === 'abuntis' && run.status === 'done' && run.meta.result_filename && (
                              run.meta.imported_at ? (
                                <span style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>
                                  Importiert ({formatDate(run.meta.imported_at)})
                                </span>
                              ) : (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setImportingRunFile(run.id);
                                  setRunImportResult(null);
                                  try {
                                    const res = await authFetch(getIngestUrl(`/dialfire/export-abuntis/results/${encodeURIComponent(run.meta.result_filename)}/stage`), { method: 'POST' });
                                    const data = await res.json();
                                    if (data.success) {
                                      navigate('/abuntis-review/' + data.jobId);
                                    } else {
                                      alert(`Staging-Fehler: ${data.error || data.message}`);
                                    }
                                  } catch (err) {
                                    alert(`Import fehlgeschlagen: ${(err as Error).message}`);
                                  }
                                  setImportingRunFile(null);
                                }}
                                disabled={importingRunFile === run.id}
                                style={{
                                  padding: '0.3rem 0.75rem',
                                  backgroundColor: importingRunFile === run.id ? '#9ca3af' : '#2563eb',
                                  color: 'white', border: 'none', borderRadius: '4px',
                                  cursor: importingRunFile === run.id ? 'not-allowed' : 'pointer', fontSize: '0.8rem',
                                }}
                              >
                                {importingRunFile === run.id ? 'Lade...' : 'Ergebnis prüfen'}
                              </button>
                              )
                            )}
                            {run.type === 'sync' && run.meta.error_message && (
                              <span style={{ color: '#dc2626', fontSize: '0.75rem' }} title={run.meta.error_message}>Fehler</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Import Result inline */}
            {runImportResult && (
              <div style={{
                padding: '1rem', borderRadius: '6px',
                backgroundColor: runImportResult.errors > 0 ? '#fffbeb' : '#f0fdf4',
                border: `1px solid ${runImportResult.errors > 0 ? '#fde68a' : '#bbf7d0'}`,
                fontSize: '0.85rem',
              }}>
                <div style={{ fontWeight: 600, color: '#166534', marginBottom: '0.5rem' }}>Import-Ergebnis (Job #{runImportResult.runId})</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
                  <div>Gesamt: <strong>{runImportResult.totalRows}</strong></div>
                  <div style={{ color: '#059669' }}>Aktualisiert: <strong>{runImportResult.updated}</strong></div>
                  <div style={{ color: '#6b7280' }}>Unverändert: <strong>{runImportResult.unchanged}</strong></div>
                  <div style={{ color: '#d97706' }}>Nicht gefunden: <strong>{runImportResult.notFound}</strong></div>
                  {runImportResult.errors > 0 && <div style={{ color: '#dc2626' }}>Fehler: <strong>{runImportResult.errors}</strong></div>}
                </div>
              </div>
            )}

            {/* Drill-Down: Zusammenfassung + Details */}
            {selectedRun && (
              <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#003366', fontSize: '1.1rem' }}>
                    Details: {selectedRun.label}
                    <span style={{
                      marginLeft: '0.75rem', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      backgroundColor: selectedRun.type === 'sync' ? '#dbeafe' : '#fce7f3',
                      color: selectedRun.type === 'sync' ? '#1e40af' : '#9d174d',
                    }}>
                      {selectedRun.type === 'sync' ? 'Sync' : 'Abuntis'}
                    </span>
                  </h3>
                  <button
                    onClick={() => { setSelectedRun(null); setLogSummary(null); setLogEntries([]); }}
                    style={{ padding: '0.4rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    Schließen
                  </button>
                </div>

                {loadingLog ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>Lade Änderungsdetails...</div>
                ) : (
                  <>
                    {/* Zusammenfassungskarten */}
                    {logSummary && logSummary.totalChanges > 0 ? (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                          {logSummary.totalChanges} Änderungen an {logSummary.totalContacts} Kontakten
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                          {logSummary.fields.map(f => (
                            <div key={f.field_name} style={{
                              padding: '0.75rem', borderRadius: '6px', backgroundColor: '#f0f4f8', border: '1px solid #e2e8f0',
                              textAlign: 'center',
                            }}>
                              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#003366' }}>{Number(f.change_count).toLocaleString()}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500 }}>{f.field_name}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : logSummary ? (
                      <div style={{ padding: '1rem', color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        Keine Feldänderungen für diesen Durchlauf protokolliert.
                      </div>
                    ) : null}

                    {/* Sync-spezifische Kennzahlen */}
                    {selectedRun.type === 'sync' && (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
                        <div style={{ padding: '0.75rem', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{Number(selectedRun.meta.total_fetched || 0).toLocaleString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Gelesen</div>
                        </div>
                        <div style={{ padding: '0.75rem', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{Number(selectedRun.meta.total_imported || 0).toLocaleString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Importiert</div>
                        </div>
                        <div style={{ padding: '0.75rem', borderRadius: '6px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>{Number(selectedRun.meta.optin_ja || 0).toLocaleString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>OptIn Ja</div>
                        </div>
                        <div style={{ padding: '0.75rem', borderRadius: '6px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{Number(selectedRun.meta.optin_nein || 0).toLocaleString()}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>OptIn Nein</div>
                        </div>
                      </div>
                    )}

                    {/* Detail-Tabelle */}
                    {logEntries.length > 0 && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{logTotal} Änderungen</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <button
                              onClick={() => loadLogPage(Math.max(1, logPage - 1))}
                              disabled={logPage <= 1}
                              style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: logPage <= 1 ? 'not-allowed' : 'pointer', opacity: logPage <= 1 ? 0.5 : 1 }}
                            >
                              <CaretLeft size={16} />
                            </button>
                            <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {logPage} / {Math.ceil(logTotal / 50) || 1}</span>
                            <button
                              onClick={() => loadLogPage(Math.min(Math.ceil(logTotal / 50), logPage + 1))}
                              disabled={logPage >= Math.ceil(logTotal / 50)}
                              style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: logPage >= Math.ceil(logTotal / 50) ? 'not-allowed' : 'pointer', opacity: logPage >= Math.ceil(logTotal / 50) ? 0.5 : 1 }}
                            >
                              <CaretRight size={16} />
                            </button>
                          </div>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Contact-ID</th>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Feld</th>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Alt</th>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Neu</th>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Zeitpunkt</th>
                              </tr>
                            </thead>
                            <tbody>
                              {logEntries.map(entry => (
                                <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{entry.contact_id}</td>
                                  <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>{entry.field_name}</td>
                                  <td style={{ padding: '0.4rem 0.5rem', color: '#dc2626', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.old_value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>leer</span>}</td>
                                  <td style={{ padding: '0.4rem 0.5rem', color: '#059669', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.new_value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>leer</span>}</td>
                                  <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                    {formatDateTime(entry.created_at)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
